import type { SupabaseClient, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { tenantIdOf } from '@/integrations/supabase/session';

// login_sessions and profiles.last_login_at are not in the generated types.
const untyped = supabase as unknown as SupabaseClient;

let recordedUserId: string | null = null;

/**
 * Best-effort login audit: one login_sessions row + profiles.last_login_at.
 * Only call after an explicit credential sign-in (never from auth events —
 * auth-js re-emits SIGNED_IN on every tab focus). Guarded per tab so a
 * double submit records once; the guard resets on sign-out.
 */
export async function recordLogin(user: User): Promise<void> {
  if (recordedUserId === user.id) return;
  recordedUserId = user.id;

  try {
    await untyped.from('login_sessions').insert({
      user_id: user.id,
      company_id: tenantIdOf(user) ?? null,
      user_agent: navigator.userAgent,
    });

    await untyped
      .from('profiles')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', user.id);
  } catch {
    // tracking must never block auth
  }
}

export function resetLoginTracking(): void {
  recordedUserId = null;
}

// An OAuth sign-in finishes on a fresh page load after the provider redirect,
// so the "this was a real sign-in" signal has to survive the round trip.
const OAUTH_PENDING_KEY = 'steddy.oauthLoginPending';

export function markOAuthLoginPending(): void {
  try {
    sessionStorage.setItem(OAUTH_PENDING_KEY, '1');
  } catch {
    // tracking must never block auth
  }
}

/** Records the login once if this page load is the return from an OAuth sign-in. */
export function recordPendingOAuthLogin(user: User): void {
  try {
    if (sessionStorage.getItem(OAUTH_PENDING_KEY) !== '1') return;
    sessionStorage.removeItem(OAUTH_PENDING_KEY);
  } catch {
    return;
  }
  void recordLogin(user);
}

export function clearPendingOAuthLogin(): void {
  try {
    sessionStorage.removeItem(OAUTH_PENDING_KEY);
  } catch {
    // ignore
  }
}
